/**
 * A small History-API router.
 *
 * Copied in shape from website-status and stripped of its locale prefix. That
 * site is public and earns `/en-gb/…` for the crawler; this one is an app
 * behind a session, with a guest link (`/m/:token`) whose whole credential is
 * the token — a locale segment in front of it would be one more thing to get
 * wrong in a URL somebody pastes into a chat.
 *
 * Still not a routing library: seven routes, no nested layouts, no loaders.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

interface RouterValue {
  path: string;
  query: URLSearchParams;
  navigate: (to: string, opts?: { replace?: boolean; keepScroll?: boolean }) => void;
}

const RouterContext = createContext<RouterValue | null>(null);

export function RouterProvider({ children }: { children: React.ReactNode }) {
  const [pathname, setPathname] = useState(() => window.location.pathname);
  const [search, setSearch] = useState(() => window.location.search);

  useEffect(() => {
    const onPop = () => {
      setPathname(window.location.pathname);
      setSearch(window.location.search);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((to: string, opts?: { replace?: boolean; keepScroll?: boolean }) => {
    const url = new URL(to, window.location.origin);
    // An external link leaves the SPA rather than being pushed onto our own
    // history stack.
    if (url.origin !== window.location.origin) {
      window.location.href = to;
      return;
    }

    if (opts?.replace) window.history.replaceState({}, '', url);
    else window.history.pushState({}, '', url);

    setPathname(url.pathname);
    setSearch(url.search);
    if (!opts?.keepScroll) window.scrollTo({ top: 0 });
  }, []);

  const value = useMemo<RouterValue>(
    () => ({ path: pathname, query: new URLSearchParams(search), navigate }),
    [pathname, search, navigate]
  );

  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRouter(): RouterValue {
  const ctx = useContext(RouterContext);
  if (!ctx) throw new Error('useRouter used outside RouterProvider');
  return ctx;
}

/** An anchor that stays inside the SPA while remaining a real link, so
 *  middle-click, ctrl-click and assistive tech all behave. */
export function Link({
  to,
  children,
  style,
  className,
  title,
  onClick
}: {
  to: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  title?: string;
  onClick?: () => void;
}) {
  const { navigate } = useRouter();

  return (
    <a
      href={to}
      className={className}
      title={title}
      style={{ textDecoration: 'none', color: 'inherit', ...style }}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        e.preventDefault();
        onClick?.();
        navigate(to);
      }}
    >
      {children}
    </a>
  );
}

/** Match `/c/:id` style patterns. Returns the params, or null. */
export function matchRoute(pattern: string, path: string): Record<string, string> | null {
  const p = pattern.split('/').filter(Boolean);
  const a = path.split('/').filter(Boolean);
  if (p.length !== a.length) return null;

  const params: Record<string, string> = {};
  for (let i = 0; i < p.length; i++) {
    const seg = p[i]!;
    if (seg.startsWith(':')) params[seg.slice(1)] = decodeURIComponent(a[i]!);
    else if (seg !== a[i]) return null;
  }
  return params;
}
